// Theme provider.
//
// Themes are CSS-variable groups on ``<html data-theme="...">`` (see
// ``src/index.css`` for the palettes). This provider exposes:
//
//   * ``theme`` — the current id ("light" | "dark" | "playful").
//   * ``setTheme(id)`` — switch; no-op for unknown ids.
//   * ``cycleTheme()`` — rotate through ``THEMES``; handy for dev toggles.
//   * ``themes`` — the catalogue (id + label + description + swatch), used
//     by ``ThemeSwitcher`` to render a radio pill.
//
// On first mount it:
//   1. Reads ``localStorage['writeon:theme']`` if set.
//   2. Otherwise honours ``prefers-color-scheme: dark``.
//   3. Falls back to ``light``.
//
// It then syncs the DOM (``<html data-theme>`` + ``color-scheme``) whenever
// the state changes and persists to storage. Storage writes are best-effort
// — we ignore failures so iframes without storage keep working.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// eslint-disable-next-line react-refresh/only-export-components
export const THEMES = [
  {
    id: 'light',
    label: 'Light',
    description: 'Clean, focused, easy on the eyes.',
    swatch: ['#0ea5e9', '#f8fafc', '#0f172a'],
  },
  {
    id: 'dark',
    label: 'Dark',
    description: 'Low-glare surfaces for long writing sessions.',
    swatch: ['#38bdf8', '#0f172a', '#f1f5f9'],
  },
  {
    id: 'playful',
    label: 'Playful',
    description: 'Warm pastels and bubbly type for creative sprints.',
    swatch: ['#ec4899', '#facc15', '#7e22ce'],
  },
  {
    id: 'warm',
    label: 'Warm',
    description:
      'Sienna Hearth — dark warm theme on a deeply saturated burnt-sienna canvas, with bright-pumpkin CTA, coral-rust accents and warm-cream ink. A confident orange-brown surround for late-evening writing.',
    // Swatch reads left→right as primary / bg / accent: Bright
    // Pumpkin CTA on Deep Burnt Sienna bg with Coral Rust as
    // the editorial accent.
    swatch: ['#e88230', '#8a3c16', '#d75a37'],
    premium_only: true,
  },
  {
    id: 'charbon',
    label: 'Charbon',
    description:
      'Charcoal Sketch — sooty charcoal-paper canvas with a hot-charcoal red CTA, aged-brass accent, and ivory-chalk ink. An artist\'s sketchpad in a lamplit studio: sooty paper under a warm hot-coal halo glowing in the bottom-right corner.',
    // primary / bg / accent: Hot Charcoal on Charcoal Paper with Aged Brass accent.
    swatch: ['#f46038', '#141216', '#c49860'],
    premium_only: true,
  },
  {
    id: 'ocean',
    label: 'Ocean',
    description:
      'Deep Sea — mid-tone saturated blue canvas with a bright cerulean-wave CTA, reef-turquoise accent, and sea-foam ink. Submerged at mid-depth in tropical water: sunlight filters down from above (bright cerulean glow at the top), deeper blue fades in below. Confidently blue and dark, but not abyssal-near-black — an in-between depth.',
    // primary / bg / accent: Cerulean Wave on Deep Sea with Reef Turquoise accent.
    swatch: ['#64c8f0', '#284880', '#2cc8b4'],
    premium_only: true,
  },
  {
    id: 'nature',
    label: 'Nature',
    description:
      'Mossbank — deep forest-green canvas dappled with sunlight through canopy, autumn-bronze accent, and parchment-cream ink. A writer\'s cabin in the deep woods.',
    // primary / bg / accent: Dappled Sun on Deep Moss with Autumn Bronze accent.
    swatch: ['#e6c65c', '#162a1e', '#c88a46'],
    premium_only: true,
  },
  {
    id: 'hc',
    label: 'Clarity',
    description:
      'Pure white background, near-black text, vivid focus rings and forced link underlines. WCAG AAA contrast.',
    swatch: ['#003cc8', '#ffffff', '#000000'],
  },
  {
    id: 'hc-dark',
    label: 'Variance',
    description:
      'Pure black background, near-white text, vivid focus rings and forced link underlines. WCAG AAA contrast.',
    swatch: ['#5ac8ff', '#000000', '#ffffff'],
  },
];

const STORAGE_KEY = 'writeon:theme';
const VALID_IDS = new Set(THEMES.map((t) => t.id));

function resolveInitialTheme() {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_IDS.has(stored)) return stored;
  } catch {
    /* ignore - storage may be blocked */
  }
  if (window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
    return 'dark';
  }
  return 'light';
}

function applyThemeToDocument(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  // ``color-scheme`` drives native form controls + the browser
  // scrollbar palette. All actually-dark-surfaced themes opt
  // into the dark schema so default scrollbars + date pickers
  // don't render bright-white on a dark surface. The ``warm``
  // palette is now a dark theme (Sienna Hearth: deep burnt-
  // sienna canvas + cream ink -- the only honest way to land a
  // confident saturated orange-brown bg without dropping AAA
  // body-text contrast) so it joins ``dark`` + ``hc-dark`` here.
  const isDark =
    theme === 'dark' ||
    theme === 'hc-dark' ||
    theme === 'warm' ||
    theme === 'charbon' ||
    theme === 'nature' ||
    theme === 'ocean';
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
}

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  cycleTheme: () => {},
  themes: THEMES,
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(resolveInitialTheme);

  useEffect(() => {
    applyThemeToDocument(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage disabled - non-fatal */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (VALID_IDS.has(next)) setThemeState(next);
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((current) => {
      const ids = THEMES.map((t) => t.id);
      const idx = ids.indexOf(current);
      return ids[(idx + 1) % ids.length];
    });
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, cycleTheme, themes: THEMES }),
    [theme, setTheme, cycleTheme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  return useContext(ThemeContext);
}
